import React, { useEffect, useMemo } from "react";

import {
  convertToCurrencyFormat,
  orderStatusOptions,
  yesOrNoOptions,
  isReadOnlyUser,
  formatDate,
  convertStatus,
} from "../../../shared/utility";
import SelectMenu from "../../controls/SelectMenu";
import Input from "../../controls/Input";
import TextArea from "../../controls/TextArea";
import OrderProductsTable from "./OrderProductsTable";
import OrderLaboursTable from "./OrderLaboursTable";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import { useForm } from "../useForm";
import ButtonDropdown from "../../controls/ButtonDropdown";
import TruckInspectionChecklist from "../shared/TruckInspectionChecklist";
import Pills from "../../controls/Pills";
import Button from "../../controls/Button";

const initialFValues = {
  id: 0,
  customer: null,
  customer_id: null,
  po_number: "",
  order_date: new Date(),
  expected_date: null,
  status: null,
  notes: "",
  items: [],
  credit_returns: [],
  currency: null,
};

const inputClassName =
  "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm";

export default function CreateNewOrderForm(props) {
  const {
    carriers,
    customers,
    isEdit,
    loading,
    order,
    setEmptyModelOpen,
    showErrorModal,
    updateOrderApiCall,
    onUpdateOrderItem,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    printOrderInvoice,
    printShippingSummary,
    printShippingDetails,
    closed,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    currencyOptions,
    manualLaboursAssigned,
    manualLaboursLoading,
    onAddManualLabour,
    onUpdateManualLabour,
    onDeleteManualLabour,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
  } = props;

  const isReadOnly = isReadOnlyUser();
  const canEditSplits =
    isEdit &&
    !isReadOnly &&
    order?.status != null &&
    ![6, 11].includes(parseInt(order.status, 10));

  const buttonOptions = [
    {
      name: "Print Invoice",
      onClick: printOrderInvoice,
      inProgress: false,
    },
    {
      name: "Print Shipping Summary",
      onClick: printShippingSummary,
      inProgress: false,
    },
    {
      name: "Print Shipping Slip",
      onClick: printShippingDetails,
      inProgress: false,
    },
  ];

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    temp.customer = null;
    if (values.customer === "") {
      temp.customer = "Please fill out field.";
    }

    temp.expected_date = null;
    if (values.expected_date === "") {
      temp.expected_date = "Please fill out field.";
    }

    temp.po_number = null;
    if (values.po_number === "") {
      temp.po_number = "Please fill out field.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  useEffect(() => {
    if (order) {
      var truckQuestion1 = [];
      if (order.truck_question_1) {
        truckQuestion1 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_1,
        );
      }

      var truckQuestion2 = [];
      if (order.truck_question_2) {
        truckQuestion2 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_2,
        );
      }

      var truckQuestion3 = [];
      if (order.truck_question_3) {
        truckQuestion3 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_3,
        );
      }

      var truckQuestion4 = [];
      if (order.truck_question_4) {
        truckQuestion4 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_4,
        );
      }

      var orderCurrency = null;
      if (order.currency) {
        orderCurrency = currencyOptions.find(
          (el) => el.value === order.currency,
        );
      }

      if (order.currency === null) {
        orderCurrency = { value: 1, label: "CAD" };
      }

      const orderCopy = {
        ...order,
        status: orderStatusOptions.filter(
          (option) => option.value === order.status,
        )[0],
        customer: customers.filter(
          (option) => option.value === order.customer_id,
        )[0],
        carrier: carriers.filter(
          (option) => option.value === order.carrier_id,
        )[0],
        truck_question_1: truckQuestion1,
        truck_question_2: truckQuestion2,
        truck_question_3: truckQuestion3,
        truck_question_4: truckQuestion4,
        currency: orderCurrency,
      };
      setValues(orderCopy);
    }
  }, [order]);

  const handleSubmit = () => {
    if (validate()) {
      updateOrderApiCall(values);
    }
  };

  const updateItemLocally = (newData, index) => {
    let currentItems = [...values.items];
    currentItems[index] = newData;
    setValues({ ...values, items: currentItems });
  };

  const onItemEditComplete = async function (newData, index) {
    if (!isEdit) return { success: false };

    const onUpdateCompleted = await onUpdateOrderItem(newData);

    if (onUpdateCompleted.success) {
      updateItemLocally(newData, index);
    }

    return onUpdateCompleted;
  };

  const makeDeleteOrderItemApiCall = async function (itemIdInDB, product_id) {
    var onItemDeleted = { success: false };
    if (isEdit) {
      onItemDeleted = await deleteOrderItemApiCall(itemIdInDB);
    }

    if (onItemDeleted.success || !isEdit) {
      const filteredItems = values.items.filter(
        (item) => item.product_id !== product_id,
      );
      setValues({ ...values, items: filteredItems });
    }
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  // Not used
  const askDeleteOrderItem = function (product) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() =>
            makeDeleteOrderItemApiCall(product.id, product.product_id)
          }
          message={`Are you sure you want to ${product.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const labourItems = manualLaboursAssigned?.items ?? [];
  const labourEntryCount = labourItems.length;
  const labourTotal = useMemo(() => {
    if (manualLaboursAssigned?.total_price != null) {
      return parseFloat(manualLaboursAssigned.total_price) || 0;
    }
    return labourItems.reduce(
      (sum, labour) => sum + (parseFloat(labour.total_price) || 0),
      0,
    );
  }, [manualLaboursAssigned]);

  const statusPill =
    isEdit && values.status?.value
      ? convertStatus(values.status.value)
      : null;

  return (
    <div>
      {order || !isEdit ? (
        <div className="flex flex-col gap-3">
          <div className="min-w-0 text-sm">
            <div className="card-custom p-2.5 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-gray-900">
                  Order details
                </h2>
                {statusPill ? <Pills content={statusPill} /> : null}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="customer"
                  required
                  options={customers.filter((option) => option.value !== -1)}
                  value={isEdit ? values.customer : null}
                  title="Customer"
                  error={errors.customer}
                  showLabel={true}
                  disabled={closed}
                />

                <Input
                  label="PO #"
                  name="po_number"
                  onChange={handleInputChange}
                  value={values.po_number}
                  labelOn={true}
                  type="text"
                  error={errors.po_number}
                  className={inputClassName}
                  disabled={closed}
                />

                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="carrier"
                  options={carriers.filter((option) => option.value !== -1)}
                  value={isEdit ? values.carrier : null}
                  title="Carrier"
                  error={errors.carrier}
                  showLabel={true}
                  disabled={closed}
                />

                {isEdit ? (
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="status"
                    options={orderStatusOptions.filter(
                      (option) => option.value !== -1,
                    )}
                    value={isEdit ? values.status : null}
                    title="Status"
                    error={errors.status}
                    showLabel={true}
                  />
                ) : null}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="currency"
                  options={currencyOptions.filter(
                    (option) => option.value !== -1,
                  )}
                  value={isEdit ? values.currency : null}
                  title="Currency"
                  error={errors.currency}
                  showLabel={true}
                  disabled={closed}
                />

                <Input
                  label="Created by"
                  name="created_by_name"
                  value={values.created_by_name}
                  labelOn={true}
                  type="text"
                  className={inputClassName}
                  disabled={true}
                />

                <Input
                  label="Shipped date"
                  name="created_at"
                  value={formatDate(values.created_at)}
                  labelOn={true}
                  type="text"
                  className={inputClassName}
                  disabled={true}
                />
              </div>

              <TruckInspectionChecklist
                values={values}
                errors={errors}
                handleInputChange={handleInputChange}
                disabled={closed}
                title="Truck inspection"
                embedded
              />

              <TextArea
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2.5 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                label="Notes"
                labelOn={true}
                name="notes"
                onChange={handleInputChange}
                value={values.notes ?? ""}
                rows="2"
                disabled={closed}
                parentClass="max-w-none"
              />

              {!isReadOnly && isEdit ? (
                <div className="flex justify-end pt-2">
                  <div className="relative z-50 overflow-visible">
                    <ButtonDropdown
                      mainOption="Save"
                      items={buttonOptions}
                      mainAction={handleSubmit}
                      variant="primary"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {values.credit_returns?.length > 0 ? (
            <div className="card-custom p-2.5">
              <p className="text-sm font-semibold text-gray-900 mb-2">
                Credit returns
              </p>
              <div className="space-y-2">
                {values.credit_returns.map((cr) => (
                  <Button
                    key={cr.id}
                    to={`/credit-returns/${cr.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-center"
                  >
                    {cr.name || `CR #${cr.id}`}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="min-w-0">
            <div className="card-custom p-2">
              <h2 className="text-base font-semibold text-gray-900 mb-3 px-1">
                Line items
              </h2>
              <OrderProductsTable
                loading={false}
                values={values}
                onItemEditComplete={onItemEditComplete}
                rowDetails={rowDetails}
                fetchRowDetails={fetchRowDetails}
                loadingRows={loadingRows}
                fetchSubRowDetails={fetchSubRowDetails}
                subRowDetails={subRowDetails}
                subLoadingRows={subLoadingRows}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                canEditSplits={canEditSplits}
                setEmptyModelOpen={setEmptyModelOpen}
                onCreatePriceSplit={onCreatePriceSplit}
                onUpdatePriceSplit={onUpdatePriceSplit}
                onDeletePriceSplit={onDeletePriceSplit}
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="card-custom p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <h2 className="text-base font-semibold text-gray-900">
                  Manual labour
                </h2>
                <p className="text-sm text-gray-500">
                  {labourEntryCount} entr
                  {labourEntryCount === 1 ? "y" : "ies"}
                  {labourTotal > 0
                    ? ` · ${convertToCurrencyFormat(labourTotal)}`
                    : ""}
                </p>
              </div>
              <OrderLaboursTable
                loading={manualLaboursLoading}
                manualLaboursAssigned={manualLaboursAssigned}
                onAddLabour={onAddManualLabour}
                onUpdateLabour={onUpdateManualLabour}
                onDeleteLabour={onDeleteManualLabour}
                closed={closed}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
